<template>
	<v-card class="event-card elevation-0 d-flex flex-column">
		<div class="event-card__container">
			<div class="event-card__outer">
				<div class="d-flex event-card__list pl-0 pr-0">
					<Banner v-if="banner" :banner="banner" />
					<EventDescription class="mt-5" :eventInfo="eventInfo" />
					<v-skeleton-loader v-if="privateLoading" v-bind="attrs" type="card" />
					<div v-else>
						<div v-if="hasButtonsBlock" class="event-card__button-section">
							<button
								class="event-card__button-section__button"
								@click="downloadICS"
								type="button"
							>
								<Icon :size="24" type="union" color="var(--button-icon-tint)" />
								<span class="event-card__button-section__button__text">
									Calendar
								</span>
							</button>
							<button
								@click="addToBookmark"
								class="event-card__button-section__button"
								type="button"
							>
								<Icon
									:type="bookmarked ? 'bookmark' : 'bookmark_empty'"
									:size="24"
									:color="'var(--button-icon-tint)'"
								/>
								<span class="event-card__button-section__button__text">
									{{ bookmarked ? 'Bookmarked' : 'Bookmark' }}
								</span>
							</button>
						</div>
						<div
							v-if="
								agendaData &&
								(agendaData.count_of_days || agendaData.count_of_sessions)
							"
							class="event-card__section"
						>
							<div class="event-card__section__agenda">
								<div class="event-card__section__agenda__counter-section">
									<div class="event-card__section__agenda__counter-section__item">
										<span
											class="event-card__section__agenda__counter-section__item__number"
											>{{ agendaData.count_of_days }}</span
										>
										<span
											class="event-card__section__agenda__counter-section__item__text"
											>days</span
										>
									</div>
									<v-divider vertical />
									<div class="event-card__section__agenda__counter-section__item">
										<span
											class="event-card__section__agenda__counter-section__item__number"
											>{{ agendaData.count_of_sessions }}</span
										>
										<span
											class="event-card__section__agenda__counter-section__item__text"
											>sessions</span
										>
									</div>
								</div>
								<div
									v-if="agendaData"
									@click="onAgenda"
									class="event-card__section__agenda__button u-cursor-pointer"
								>
									{{ agendaData.btn_title }}
									<Icon
										type="chevron_right"
										:size="24"
										color="var(--button-secondary-background-color)"
									/>
								</div>
							</div>
						</div>
						<div v-if="agendaDetails" class="event-card__section">
							<v-card-text class="event-card__section-title"> Details</v-card-text>
							<ExpandableHtml :content="agendaDetails.event_description" />
						</div>
						<div
							v-if="restrictSection && restrictSection.restrict_event_text"
							class="event-card__restrict"
						>
							{{ restrictSection.restrict_event_text.replace('. ', '.\n') }}
						</div>
						<div class="event-card__section" v-if="hasSpeakers">
							<div class="d-flex justify-space-between mb-3">
								<v-card-text class="event-card__section-title">
									Speakers</v-card-text
								>
								<div class="event-card__show-btn">
									<router-link
										:to="{
											name: 'event-speakers',
											params: { id: this.eventInfo.event_id },
											query: {
												event_id: $route.params.id,
											},
										}"
										@click.native="handleShowAllSpeakers"
									>
										See All
									</router-link>
								</div>
							</div>
							<div class="d-flex justify-space-around">
								<div
									v-for="speaker in speakers.speakers_list.slice(0, 7)"
									:key="speaker.speaker_id"
								>
									<router-link
										:to="{
											name: 'speaker-by-id',
											params: {
												id: speaker.speaker_id,
											},
											query: {
												event_id: $route.params.id,
											},
										}"
										@click.native.prevent="handleToSpeaker(speaker.speaker_id)"
									>
										<Avatar
											class="event-card__avatar mr-2 mb-1 border-radius-50"
											:size="60"
											:avatar="speaker.speaker_logo"
											:defaultAvatar="'speaker'"
											:color="'#ffffff'"
										/>
									</router-link>
								</div>
							</div>
						</div>
						<div class="event-card__section" v-if="hasAttendees">
							<div class="d-flex justify-space-between">
								<v-card-text class="event-card__section-title mb-3">
									Attendees</v-card-text
								>
								<div class="event-card__show-btn" @click="handleClickAttendees">
									<router-link
										:to="{
											name: 'attendees',
											params: { id: eventInfo.event_id },
										}"
									>
										See All
									</router-link>
								</div>
							</div>

							<div class="d-flex flex-wrap justify-space-around">
								<div
									v-for="(user, index) in attendeesSection.attendees_list.slice(
										0,
										8
									)"
									:key="index"
									@click="handleClickAttendee(user.user_id)"
								>
									<router-link
										:to="{
											name: isCurrentUser(user.user_id)
												? 'my-account'
												: 'user-by-id',
											params: isCurrentUser(user.user_id)
												? []
												: { id: user.user_id },
										}"
										class="text-decoration-none"
									>
										<Avatar
											class="event-card__avatar border-radius-50"
											:size="60"
											:avatar="user.user_logo"
										/>
									</router-link>
								</div>
							</div>
						</div>
						<div v-if="exhibitorsSection">
							<div
								class="event-card__section"
								v-for="(
									{
										group_name = '',
										event_map_url = '',
										partners = [],
										group_id = 0,
										event_id = 0,
										partner_per_group = 8,
									},
									index
								) in exhibitorsSection.partnerGroups"
								:key="index"
							>
								<div class="d-flex justify-space-between">
									<v-card-text class="event-card__section-title mb-3">
										{{ group_name }}
									</v-card-text>
									<div
										class="event-card__show-btn"
										@click="handleShowAllExhibitors(group_name)"
									>
										<router-link
											:to="{
												name: 'partners',
												params: { id: group_id, name: group_name },
												query: {
													event: event_id,
													name: group_name,
												},
											}"
										>
											See All
										</router-link>
									</div>
								</div>

								<div class="event-card__exhibitors-container-wrapper">
									<div
										v-for="(partner, index) in partners.slice(
											0,
											partner_per_group
										)"
										:key="index"
										@click="handleExhibitorClick(partner)"
										class="event-card__exhibitors-container-wrapper__exhibitors-item"
									>
										<router-link
											class="event-card__exhibitors-container-wrapper__link"
											:to="{
												name: 'exhibitor-by-id',
												params: {
													id: partner.partner_id,
												},
												query: {
													group: group_id,
													event: event_id,
												},
											}"
										>
											<img
												class="event-card__exhibitors-container-wrapper__exhibitors-item__exhibitors-logo"
												loading="lazy"
												:src="partner.partner_logo"
												alt="avatar"
											/>
										</router-link>
									</div>
								</div>
								<div
									@click="handleToMapTrigger(group_name)"
									v-if="event_map_url"
									class="mt-3"
								>
									<button
										type="button"
										@click="toggleOpenMap(event_map_url)"
										class="event-card__section__map-btn"
									>
										Floor Map
										<Icon
											type="chevron_right"
											color="var(--button-secondary-background-color)"
											:size="18"
										/>
									</button>
								</div>
							</div>
						</div>

						<div
							v-if="additionalInfo && additionalInfo.eventActions.length"
							class="event-card__section mb-2"
						>
							<div>
								<v-card-text class="event-card__section-title mb-3">
									Additional Info
								</v-card-text>

								<div class="event-card__event-actions">
									<ActionPreview
										v-for="(action, index) in additionalInfo.eventActions"
										:key="index"
										class="event-card__event-actions__event-wrapper"
										:event-id="eventInfo.event_id"
										:action="action"
										@click.native="
											handleEventActionClick(action, action.action_id, $event)
										"
									>
										<div class="event-card__action-wrapper">
											<Icon
												:type="getAction(action).icon"
												color="var(--button-icon-tint)"
												:size="24"
											/>
										</div>
										<div
											class="event-card__event-actions__title text-decoration-none"
										>
											{{ getAction(action).name }}
										</div>
									</ActionPreview>
								</div>
							</div>
						</div>
						<div v-if="isFeedbackVisible" class="event-card__section">
							<h4 class="event-card__section-title mb-3">Feedback</h4>
							<div class="event-card__section__feedback">
								<button
									v-if="feedbackSection.qa"
									@click="
										toggleToQaNa(
											feedbackSection.qa.qna_id,
											feedbackSection.qa.qna_entity_id,
											feedbackSection.qa.qna_entity_type,
											feedbackSection.qa.qna_allow_anonymous
										)
									"
									type="button"
									class="event-card__section__feedback__button-survey"
								>
									<Icon
										type="question"
										:size="24"
										color="var(--button-icon-tint)"
									/>
									<span
										class="event-card__section__feedback__button-survey__text"
										>{{ feedbackSection.qa.button_name }}</span
									>
								</button>
								<button
									v-if="feedbackSection.surveys"
									@click="
										toggleToSurvey(
											feedbackSection.surveys.survey_id,
											feedbackSection.surveys.survey_type
										)
									"
									type="button"
									class="event-card__section__feedback__button-survey"
								>
									<CustomCheckbox
										v-if="feedbackSection.surveys.is_complete"
										:checkRadius="10"
										:isChecked="!!feedbackSection.surveys.is_complete"
										class="event-card__section__feedback__button-survey__checkbox"
									/>
									<Icon
										type="task-list"
										:size="24"
										:color="
											feedbackSection.surveys.is_complete
												? '#9b9b9b'
												: 'var(--button-icon-tint)'
										"
									/>
									<span
										:style="{
											color: feedbackSection.surveys.is_complete
												? '#9b9b9b'
												: 'var(--username-category-title)',
										}"
										class="event-card__section__feedback__button-survey__text"
										>{{ feedbackSection.surveys.button_name }}</span
									>
								</button>
							</div>
							<div
								v-if="feedbackSection.rating"
								class="event-card__section__ratings mt-2"
							>
								<Rating
									:rating="feedbackSection.rating"
									@eventRatingTrigger="eventRatingTrigger"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<InfoModal :isModal="isModal" @closeModal="closeModal" :isCompleted="true" />
			<v-dialog
				v-model="mobileLink"
				class="exhibitor-page__modal"
				max-width="588px"
				width="100%"
				persistent
			>
				<v-card class="exhibitor-page__modal">
					<div class="exhibitor-page__modal__title">
						This screen is not available in the Web App, but you can access it through
						the mobile app
					</div>
					<button
						type="button"
						@click="toggleCloseMobileLink"
						class="exhibitor-page__modal__btn"
					>
						OK
					</button>
				</v-card>
			</v-dialog>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import ActionPreview from '@/components/Event/Action/ActionPreview'
import Rating from '@/components/Event/Rating'
import Dwh from '@/mixins/Dwh'
import EventDescription from '@/components/Event/EventDescription.vue'
import Banner from '@/components/Banner.vue'
import CustomCheckbox from '@/components/CustomCheckbox.vue'
import InfoModal from '@/components/Modals/InfoModal.vue'
import ExpandableHtml from '@/components/ExpandableHtml.vue'

Vue.use(VueScrollTo)

export default {
	mixins: [Dwh],
	components: {
		InfoModal,
		CustomCheckbox,
		Banner,
		EventDescription,
		ActionPreview,
		Avatar,
		Rating,
		ExpandableHtml,
	},
	props: {
		event: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			bookmarked: null,
			isSchedule: 0,
			isModal: false,
			mobileLink: false,
			changeContent: false,
			offset: true,
			polls_answers: [],
			custom_answer: '',
			isVoteDirty: false,
			rating_answers: [],
			isFollowed: false,
			attrs: {
				elevation: 0,
			},
		}
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated', 'mapRegisterToken']),
		...mapGetters('event', ['isLoading', 'pollsLoading', 'ratingsLoading', 'privateLoading']),
		...mapGetters('advertisement', ['banner']),
		...mapGetters('settings', ['eventSettings', 'appCore']),
		isFeedbackVisible() {
			return (
				this.feedbackSection &&
				(this.feedbackSection.rating ||
					this.feedbackSection.qa ||
					this.feedbackSection.surveys)
			)
		},
		currentUser() {
			return this.profile
		},
		buttonsBlock() {
			return this.getBlockData('buttons', {})
		},
		hasButtonsBlock() {
			const b = this.buttonsBlock
			return !!(
				b &&
				((Array.isArray(b.calendar) && b.calendar.length) ||
					(b.bookmark !== undefined && b.bookmark !== null) ||
					(Array.isArray(b.share) && b.share.length))
			)
		},
		restrictSection() {
			return this.getBlockData('eventRestrictedAccess', {})
		},
		eventInfo() {
			return this.getBlockData('eventInfo', {})
		},
		agendaDetails() {
			return this.getBlockData('details', {})
		},
		agendaData() {
			return this.getBlockData('agendaData', {})
		},
		speakers() {
			return this.getBlockData('speakers', {})
		},
		attendeesSection() {
			return this.getBlockData('attendees', {})
		},
		feedbackSection() {
			return this.getBlockData('feedback', {})
		},
		exhibitorsSection() {
			const res = this.getBlockData('exhibitors', { partnerGroups: [] })
			return {
				...res,
				partnerGroups: Array.isArray(res.partnerGroups) ? res.partnerGroups : [],
			}
		},
		hasSpeakers() {
			return (
				Array.isArray(this.speakers?.speakers_list) &&
				this.speakers.speakers_list.length > 0
			)
		},
		hasAttendees() {
			return (
				Array.isArray(this.attendeesSection?.attendees_list) &&
				this.attendeesSection.attendees_list.length > 0
			)
		},
		additionalInfo() {
			const res = this.getBlockData('additionalInfo', { eventActions: [] })
			return {
				...res,
				eventActions: Array.isArray(res.eventActions) ? res.eventActions : [],
			}
		},
		polls() {
			return this.event.polls.filter(
				(poll) =>
					poll.pollsAnswers.length ||
					(!poll.pollsAnswers.length && poll.allow_custom_answer)
			)
		},
	},
	watch: {
		event: {
			handler() {
				this.eventViewDetails()
				this.bookmarked = this.buttonsBlock?.bookmark?.is_bookmarked ?? false
			},
			immediate: true,
		},
	},
	methods: {
		...mapActions('advertisement', ['getBanner', 'clearBanner']),
		...mapActions('post', ['getPost', 'deletePost']),
		...mapActions('event', [
			'addEventToSchedule',
			'removeEventFromSchedule',
			'voteEventPolls',
			'voteEventRatings',
			'toggleBookmark',
		]),
		...mapActions('app', ['setPageTitle']),
		...mapActions('auth', ['getMapRegisterToken']),
		eventRatingTrigger(value) {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_rating_event',
				module: 'Events',
				title: this.eventInfo.event_title,
				id2: this.eventInfo.event_id,
				data: value,
			})
		},
		eventViewDetails() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'view_event_details',
				module: 'Events',
				title: this.eventInfo.event_title,
				id1: this.eventInfo.event_id,
			})
		},
		closeModal() {
			return (this.isModal = false)
		},
		handleToSpeaker(id) {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_speaker_details_event',
				module: 'Events',
				id1: id,
				id2: this.eventInfo.event_id,
				title: this.eventInfo.event_title,
			})
		},
		handleToSurveyEvent() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_surveys_event',
				module: 'Events',
				id2: this.eventInfo.event_id,
				title: this.eventInfo.event_title,
			})
		},
		toggleToSurvey(id, type) {
			if (this.feedbackSection.surveys.is_complete === 1) {
				return (this.isModal = true)
			}
			this.handleToSurveyEvent()
			this.$router.push({
				name: 'survey',
				query: {
					survey_id: id,
					survey_type: type,
					event_id: this.$route.params.id,
				},
			})
		},
		handleToQaEvent() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_q_a_event',
				module: 'Events',
				title: this.eventInfo.title,
				id2: this.eventInfo.event_id,
			})
		},
		toggleToQaNa(id, qna_entity_id, qna_entity_type, qna_allow_anonymous) {
			this.handleToQaEvent()
			this.$router.push({
				name: 'event-session-questions',
				query: {
					qna_id: id,
					qna_entity_id: qna_entity_id,
					qna_entity_type: qna_entity_type,
					qna: qna_allow_anonymous ? 1 : 0,
				},
			})
		},
		async addToBookmark() {
			const newStatus = this.bookmarked ? 0 : 1
			this.sendTrigger({
				category: 'Event Details',
				action: newStatus === 1 ? 'click_bookmark_event' : 'click_remove_bookmark_event',
				module: 'Events',
				title: this.eventInfo.event_title,
				id2: this.eventInfo.event_id,
			})
			await this.toggleBookmark({
				user_id: this.currentUser.user_id,
				bookmark_entity_type: this.buttonsBlock.bookmark.bookmark_entity_type,
				bookmark_status: newStatus,
				bookmark_entity_id: this.buttonsBlock.bookmark.bookmark_entity_id,
			})

			this.bookmarked = newStatus
		},
		getBlockData(name, fallback = undefined) {
			const block = (this.event.eventView || []).find((b) => b.block_name === name)
			return block ? block.block_data : fallback
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
		handleCalendarTrigger() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_calendar_event',
				module: 'Events',
				title: this.eventInfo.event_title,
				id2: this.eventInfo.event_id,
			})
		},
		downloadICS() {
			this.handleCalendarTrigger()
			const title = this.eventInfo.event_title || 'Untitled Event'
			const description = (this.agendaDetails.event_description || '')
				.replace(/<\/?[^>]+(>|$)/g, '')
				.replace(/<!--[\s\S]*?-->/g, '')
			const location = this.eventInfo
				? `${this.eventInfo.address?.address_line1 || ''} ${
						this.eventInfo.address?.address_line2 || ''
				  } ${this.eventInfo.address?.address_city || ''}`.trim()
				: 'Online'
			const linkToEvent = `${window.location.origin}/webapp/events/${this.eventInfo.event_id}`
			const tzid = this.eventInfo.eventTimezone?.timezone_utc || 'UTC'

			function formatICSDate(unix) {
				const d = new Date(unix * 1000)
				const pad = (n) => (n < 10 ? '0' + n : n)
				return (
					d.getFullYear() +
					pad(d.getMonth() + 1) +
					pad(d.getDate()) +
					'T' +
					pad(d.getHours()) +
					pad(d.getMinutes()) +
					pad(d.getSeconds())
				)
			}

			const dtstart = formatICSDate(this.eventInfo.event_start)
			const dtend = formatICSDate(this.eventInfo.event_end)
			const uid = `${this.eventInfo.event_id}@yourapp.com`
			const dtstamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'

			const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
CALSCALE:GREGORIAN
PRODID:-//YourApp//EN
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${dtstamp}
DTSTART;TZID=${tzid}:${dtstart}
DTEND;TZID=${tzid}:${dtend}
SUMMARY:${title}
DESCRIPTION:${description}
LOCATION:${location}
URL:${linkToEvent}
END:VEVENT
END:VCALENDAR`.trim()

			const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
			const url = URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `${title}.ics`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
		},
		handleToAgenda() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_agenda_button_event',
				module: 'Events',
				title: this.eventInfo.event_title,
				id2: this.eventInfo.event_id,
			})
		},
		onAgenda() {
			this.handleToAgenda()
			this.$router.push({
				name: 'event-agenda',
				params: {
					id: this.$route.params.id,
				},
			})
		},
		isMapUrlValid(url) {
			try {
				const arr = JSON.parse(this.appCore.parsing_url)
				const link = new URL(url)
				return arr.some((allowed) => link.origin === allowed)
			} catch (e) {
				console.error(e)
				return false
			}
		},
		toggleCloseMobileLink() {
			this.mobileLink = false
		},
		handleToMapTrigger(groupName) {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_exhibitor_map',
				module: 'Events',
				id2: this.eventInfo.event_id,
				title: groupName,
			})
		},
		async toggleOpenMap(url) {
			if (!this.event) return

			const mapUrl = url

			if (mapUrl && this.isMapUrlValid(mapUrl)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) {
						console.error('Token not received')
						return
					}
					const urlWithToken = `${mapUrl}${mapUrl.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(urlWithToken, '_self')
				} catch (e) {
					console.error(e)
				}
			} else if (mapUrl) {
				window.open(mapUrl, '_self')
			}
		},
		checkContentHeight() {
			const contentEl = this.$refs.contentInner
			if (contentEl) {
				const lineHeight = parseFloat(window.getComputedStyle(contentEl).lineHeight)
				const height = contentEl.scrollHeight
				const numLines = Math.floor(height / lineHeight)
				this.isContentTooLong = numLines > 5
			}
		},

		getAction(action) {
			let icons = {
				link: 'link',
				email: 'email',
				phone: 'phone',
				grid: 'phone_empty',
				fileList: 'folder',
				linkList: 'list',
				deepLink: 'link',
				grid2: 'IconGrid2',
				map: 'location',
			}
			return {
				icon: icons[action.action_type],
				link:
					action.action_type !== '' && action.action_type !== ''
						? action.action_value
						: null,
				name: action.action_name,
				color: action.button_color,
			}
		},
		handleShowAllExhibitors(title) {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_exhibitors_list_event',
				module: 'Events',
				title: title,
				id2: this.eventInfo.event_id,
			})
		},
		handleExhibitorClick(partner) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_exhibitor_profile_event',
				module: 'Events',
				id1: partner.partner_id,
				id2: this.eventInfo.event_id,
				title: partner.partner_name,
			})
		},
		handleClickAttendees() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_attendees_list_event',
				module: 'Events',
				id2: this.eventInfo.event_id,
				title: this.eventInfo.event_title,
			})
		},
		handleClickAttendee(id) {
			this.sendTrigger({
				category: 'Attendees',
				action: 'click_attendee_profile_event',
				module: 'Events',
				id1: id,
				id2: this.eventInfo.event_id,
			})
		},
		async handleEventActionClick(eventAction, id, event) {
			let action = null
			switch (eventAction.action_type) {
				case 'map':
					action = 'click_map_event'
					break
				case 'email':
					action = 'click_action_email_event'
					break
				case 'phone':
					action = 'click_action_phone_event'
					break
				case 'link':
					action = 'click_action_link_event'
					break
				case 'deepLink':
					action = 'click_deeplink_event'
					break
				case 'linkList':
					action = 'click_action_list_of_links_event'
					break
				case 'fileList':
					action = 'click_action_list_of_files_event'
					break
				default:
					action = null
			}
			if (eventAction.action_type === 'link' || eventAction.action_type === 'map') {
				try {
					await this.toggleOpenMap(eventAction.action_value)
				} catch (e) {
					console.error(e)
				} finally {
					await this.sendTrigger({
						category: 'Event Details',
						action,
						module: 'Events',
						id2: this.eventInfo.event_id,
						title: eventAction.action_name,
					})
				}
				return
			}

			if (eventAction.action_type === 'deepLink') {
				await this.sendTrigger({
					category: 'Event Details',
					action,
					module: 'Events',
					id2: this.eventInfo.event_id,
					title: eventAction.action_name,
				})
				const restrictedLinks = [
					'live-events',
					'event-manager',
					'higher-logic',
					'gamification',
				]
				const lowerCaseLink = eventAction.action_value.toLowerCase()
				const isRestricted = restrictedLinks.some((restricted) =>
					lowerCaseLink.includes(restricted)
				)

				if (isRestricted) {
					if (event) {
						event.preventDefault()
						event.stopPropagation()
					}
					this.mobileLink = true
					return
				} else {
					const target = this.transformDeepLink(eventAction.action_value)
					window.open(target, '_blank')
					return
				}
			}
			if (action) {
				await this.sendTrigger({
					category: 'Event Details',
					action,
					module: 'Events',
					title: eventAction.action_name,
					id1: id,
					id2: this.eventInfo.event_id,
				})
			}
		},

		transformDeepLink(link) {
			const match = link.match(/:\/\/(.*)/)
			return match ? `${window.location.origin}/webapp/${match[1]}` : link
		},

		handleShowAllSpeakers() {
			this.sendTrigger({
				category: 'Event Details',
				action: 'click_speaker_list_event',
				module: 'Events',
				title: this.eventInfo.event_title,
				id2: this.eventInfo.event_id,
			})
		},
		answerChange(answer) {
			if (!answer.answer || (Array.isArray(answer.answer) && !answer.answer.length)) {
				if (this.polls_answers.length) {
					this.polls_answers = this.polls_answers.filter(
						(poll) => poll.poll_id !== answer.poll_id
					)
				}
			} else {
				const pollAnswer = this.polls_answers.find(
					(poll) => poll.poll_id === answer.poll_id
				)
				if (pollAnswer) {
					this.polls_answers = this.polls_answers.map((poll) => {
						if (poll.poll_id === answer.poll_id) {
							return answer
						} else {
							return poll
						}
					})
				} else {
					this.polls_answers = [...this.polls_answers, answer]
				}
			}
		},
	},
	created() {
		if (this.$route.params.id !== undefined) {
			const bannerQuery = {
				places: ['event_general'],
				place_sub_id: this.$route.params.id,
			}
			this.getBanner(bannerQuery)
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.checkContentHeight()
		})
		this.setPageTitle('Events')
	},
	beforeDestroy() {
		this.setPageTitle('')
	},
}
</script>

<style lang="scss">
.fade-overlay {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 30px;
}
.event-card {
	background-color: transparent !important;
	font-family: 'Inter', sans-serif;
	&__restrict {
		width: 100%;
		height: 217px;
		background-color: #f2f2f2;
		display: flex;
		justify-content: center;
		align-items: center;
		font-weight: 700;
		font-size: 16px;
		line-height: 100%;
		letter-spacing: 0;
		text-align: center;
		white-space: pre-line;
		color: #4a4a4a;
	}
	&__button-section {
		margin-bottom: 8px;
		display: flex;
		gap: 8px;
		&__button {
			display: flex;
			flex-direction: column;
			gap: 8px;
			padding: 12px;
			align-items: center;
			justify-content: center;
			width: 100%;
			border: 1px solid #e2e2e2;
			background-color: #ffffff;
			border-radius: 8px;
			&__text {
				font-weight: 500;
				font-size: 16px;
				line-height: 100%;
				letter-spacing: 0;
				color: var(--username-category-title);
			}
		}
	}
	&__event-actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 8px;
		&__event-wrapper {
			display: flex;
			flex: 0 0 calc(50% - 4px);
			box-sizing: border-box;
			align-items: center;
			padding: 8px;
			gap: 16px;
			width: 100%;
			background-color: #f3f3f6;
			border-radius: 8px;
		}
		&__title {
			font-family: 'Inter', sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 140%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}
	&__exhibitors-container-wrapper {
		display: flex;
		justify-content: space-around;
		&__link {
			display: flex;
			justify-content: center;
			align-items: center;
			border: 1px solid #e2e2e2;
			border-radius: 8px;
		}
		&__exhibitors-item {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 60px;
			height: 60px;
			object-fit: contain !important;
			&__exhibitors-logo {
				object-fit: contain !important;
				border-radius: 8px;
				width: 60px;
				height: 60px;
			}
		}
	}

	&__section {
		padding: 12px;
		background: #fff;
		margin-bottom: 8px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
		&__empty {
			width: 100%;
			height: 92px;
			display: flex;
			justify-content: center;
			align-items: center;
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			vertical-align: middle;
			color: #4a4a4a;
		}
		&__feedback {
			display: flex;
			gap: 8px;
			&__button-survey {
				font-family: 'Inter', sans-serif;
				width: 100%;
				padding: 12px;
				height: 75px;
				background: #f3f3f6;
				border-radius: 8px;
				display: flex;
				flex-direction: column;
				justify-content: center;
				align-items: center;
				gap: 8px;
				position: relative;
				&__checkbox {
					position: absolute;
					right: 4px;
					top: 4px;
				}
				&__text {
					font-weight: 500;
					font-size: 16px;
					line-height: 100%;
					letter-spacing: 0;
					color: var(--username-category-title);
				}
			}
		}
		&__agenda {
			display: flex;
			justify-content: space-between;
			gap: 8px;
			&__counter-section {
				width: 100%;
				display: flex;
				justify-content: space-around;
				height: 46px;
				&__item {
					display: flex;
					width: 100%;
					flex-direction: column;
					justify-content: center;
					align-items: center;
					gap: 2px;
					font-family: 'Inter', sans-serif;
					&__number {
						font-weight: 700;
						font-size: 20px;
						line-height: 22px;
						letter-spacing: 0;
						text-align: center;
						color: var(--username-category-title);
					}
					&__text {
						font-weight: 400;
						font-size: 16px;
						line-height: 140%;
						letter-spacing: 0;
						text-align: center;
						color: #9b9b9b;
					}
				}
			}
			&__button {
				width: 100%;
				height: 46px;
				display: flex;
				gap: 8px;
				justify-content: center;
				align-items: center;
				border-radius: 8px;
				border: 1.5px solid var(--button-secondary-background-color);
				color: var(--button-secondary-background-color);
				font-family: 'Inter', sans-serif;
				font-weight: 700;
				font-size: 18px;
				line-height: 100%;
				letter-spacing: 0;
			}
		}
		&__map-btn {
			display: flex;
			justify-content: center;
			border-radius: 8px;
			align-items: center;
			gap: 8px;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			width: 100%;
			letter-spacing: 0;
			height: 44px;
			text-align: center;
			border: 1.5px solid var(--button-secondary-background-color);
			color: var(--button-secondary-background-color);
		}

		&-title {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		margin-bottom: 30px;
	}

	&__content-inner {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 18px;
		padding-top: 10px;

		img {
			width: 100%;
		}

		p {
			margin-bottom: 0;
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}
		a {
			color: var(--primary_color);
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: rgb(66, 160, 216);
		letter-spacing: 0.36px;
		gap: 4px;

		&-bottom {
			color: rgb(155, 155, 155);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			line-height: 16px;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: right;
		}
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: rgb(74, 74, 74);
		font-size: 24px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
	}

	&__event-time {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: 600;
	}

	&__attendees {
		&-member {
			width: 50px;
			height: 50px;
			border-radius: 50%;
			object-fit: cover;
		}

		&-last-member {
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 999;
			background-color: #ffffff;
			border: 1px solid #e2e2e2;
			color: #4a4a4a;
			display: flex;
			justify-content: center;
			align-items: center;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;

			width: 60px;
			height: 60px;
			border-radius: 50%;
		}

		&-last-container {
			position: relative !important;
			width: 60px !important;
		}
	}

	&__event {
		&-wrapper {
			display: flex;
			align-items: center;
			gap: 10px;
			width: 250px;
			margin-bottom: 20px;
			margin-right: 10px;

			i {
				border-radius: 50%;
				min-width: 40px;
				min-height: 40px;
			}
		}

		&-title {
			color: var(--ceil-detail-text-color);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
		}
	}

	&__exhibitors {
		&-container {
			display: grid;
			grid-template-columns: repeat(3, 1fr);
			flex-wrap: wrap;
			align-items: center;
			padding: 10px 20px;
			gap: 16px;
			justify-content: space-between !important;

			@media screen and (max-width: 600px) {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				padding: 10px 20px;
				gap: 16px;
				justify-content: space-between !important;
			}
		}

		&-more {
			color: var(--accent-color);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-decoration: underline;
			text-align: right;
		}

		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: center;

		img {
			width: 120px;
		}
	}

	&__subtitle {
		&-category {
			color: rgb(155, 155, 155);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}
	}

	&__registered {
		color: rgb(255, 255, 255) !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-align: center;
		background-color: #a0a0a0 !important;
		border-radius: 8px;
		height: 50px !important;
		width: 100%;
	}

	&__register {
		color: rgb(255, 255, 255) !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-align: center;
		background-color: var(--button-secondary-background-color) !important;
		border-radius: 8px;
		height: 50px !important;
		width: 100%;
	}

	&__action-wrapper {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&_brief-description {
		font-family: 'Montserrat', sans-serif;
		font-size: 14px;
		color: #002855;
		line-height: 17.07px;
		font-weight: 400;
	}

	&__description {
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
		color: #4a4a4a;
		p {
			margin: 0 0 8px !important;
		}
		a {
			color: var(--primary_color) !important;
		}
		&.collapsed {
			padding-top: 6px;
			display: -webkit-box;
			-webkit-line-clamp: 5;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}
		&__show-more {
			display: flex;
			justify-content: flex-end;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--primary_color);
		}

		table {
			margin: 15px auto;
			border-collapse: collapse;
			font-family: 'Inter', sans-serif;
		}

		table td {
			padding: 15px;
			text-align: center;
		}

		table th {
			padding: 15px;
		}

		table thead td {
			background-color: #54585d;
			color: #ffffff;
			font-weight: bold;
			font-size: 13px;
			border: 1px solid #54585d;
			text-align: center;
		}

		table thead th {
			border: 1px solid #dddfe1;
		}

		table tbody td {
			color: #636363;
			border: 1px solid #dddfe1;
		}

		table tbody th {
			border: 1px solid #dddfe1;
		}

		table tbody tr {
			background-color: #f9fafb;
		}

		table tbody tr:nth-child(odd) {
			background-color: #ffffff;
		}

		img {
			width: 100%;
		}
	}

	&__tag {
		padding: 6px;
		border-radius: 3px !important;
		color: rgb(74, 74, 74);
		font-size: 14px !important;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		height: 20px !important;
		cursor: pointer;

		span {
			min-width: 100% !important;
			width: 100%;
		}
	}

	&__schedule-btn,
	&__show-btn {
		display: flex;
		justify-content: center;
		width: 64px;
		white-space: nowrap;

		a {
			color: var(--text_button_tint) !important;
			font-family: 'Inter', sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			text-decoration: unset;
		}
	}

	&__poll-btn,
	&__rating-btn {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px !important;
		line-height: 22px;
		font-weight: 700;
		text-transform: capitalize;
		background-color: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;

		&-disabled {
			opacity: 0.5;
			cursor: default;
		}
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}

		&__list {
			flex: 0 0 75%;
		}
	}

	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}

		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}

	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
			border-radius: 10px;

			&-image {
				max-height: 250px;
			}
		}

		&__default-thumbnail {
			height: 100%;
		}

		&__outer {
			flex-direction: column;
		}
	}
}

@keyframes runningText1 {
	// 0 - 40
	0% {
		transform: translateX(100%);
	}

	100% {
		transform: translateX(-50%);
	}
}

@keyframes runningText2 {
	// 50 - 180
	0% {
		transform: translateX(100%);
	}

	100% {
		transform: translateX(-100%);
	}
}
</style>
